import { formatPhoneNumber } from 'shared-utils/utils/formatPhoneNumber';
import { formatStandardDateLocaleString, formatStandardDate } from 'shared-utils/utils/formatDate';
import { emptyTableCellCopy } from 'design-system/src/sharedCopy';
import { Address, Drug, Patient, Prescriber, Pharmacy, Insurance } from '../../api';
import {
  SidebarRow,
  SidebarRowData,
  SidebarRowLabel,
  SidebarSection,
} from 'design-system/src/components/Sidebar/Sidebar';
import ButtonRow, { Alignment } from 'design-system/src/components/ButtonRow/ButtonRow';
import React, { useState } from 'react';
import styled from 'styled-components';
import { NavLink } from 'react-router-dom';
import { standardTransitionStyles } from 'design-system/src/sharedStyles';
import BusinessRelationshipInfoBox from '../businessRelationshipNotices/BusinessRelationshipInfoBox/BusinessRelationshipInfoBox';
import Button from 'design-system/src/components/Button/Button';
import PharmacySearchModal from '../PharmacySearch/PharmacySearchModal';

const StyledLink = styled(NavLink)`
  color: var(--grayscale-900);
  ${standardTransitionStyles}

  // not using the standard focusVisibleStyles because it looks weird on a wrapping link
  &:focus-visible {
    outline-color: var(--purple-700);
  }
`;

export const PatientPanel: React.FC<{ patient: Patient }> = ({ patient }) => {
  return (
    <SidebarSection>
      <SidebarRow>
        <SidebarRowLabel>Patient</SidebarRowLabel>
        <SidebarRowData>
          {patient.first_name} {patient.last_name}
        </SidebarRowData>
      </SidebarRow>
      <SidebarRow>
        <SidebarRowLabel>DOB</SidebarRowLabel>
        <SidebarRowData>{formatStandardDate(patient.date_of_birth)}</SidebarRowData>
      </SidebarRow>
      <SidebarRow>
        <SidebarRowLabel>Phone</SidebarRowLabel>
        <SidebarRowData>{formatPhoneNumber(patient.phone_number)}</SidebarRowData>
      </SidebarRow>
      <AddressRow address={patient.address} />
    </SidebarSection>
  );
};

export const PrescriberPanel: React.FC<{ prescriber: Prescriber }> = ({ prescriber: prescriber }) => {
  return (
    <SidebarSection>
      <SidebarRow>
        <SidebarRowLabel>Prescriber</SidebarRowLabel>
        <SidebarRowData>{prescriber.name}</SidebarRowData>
      </SidebarRow>
      <SidebarRow>
        <SidebarRowLabel>NPI</SidebarRowLabel>
        <SidebarRowData>{prescriber.npi}</SidebarRowData>
      </SidebarRow>
      <SidebarRow>
        <SidebarRowLabel>Phone</SidebarRowLabel>
        <SidebarRowData>{formatPhoneNumber(prescriber.phone_number)}</SidebarRowData>
      </SidebarRow>
      <SidebarRow>
        <SidebarRowLabel>Fax</SidebarRowLabel>
        <SidebarRowData>{formatPhoneNumber(prescriber.fax_number)}</SidebarRowData>
      </SidebarRow>
      <AddressRow address={prescriber.address} />
    </SidebarSection>
  );
};

export const DrugPanel: React.FC<{
  drug: Drug;
  linkToRxPage?: boolean;
  showBusinessRelationshipNotice?: boolean;
  businessRelationshipNoticeCopy?: string;
}> = ({ drug, linkToRxPage = true, showBusinessRelationshipNotice = false, businessRelationshipNoticeCopy }) => {
  return (
    <SidebarSection>
      {showBusinessRelationshipNotice && <BusinessRelationshipInfoBox customCopy={businessRelationshipNoticeCopy} />}
      <SidebarRow>
        <SidebarRowLabel>Drug</SidebarRowLabel>
        <SidebarRowData>
          {linkToRxPage ? <StyledLink to={`/rxs/${drug.id}`}>{drug.name}</StyledLink> : drug.name}
        </SidebarRowData>
      </SidebarRow>
      <SidebarRow>
        <SidebarRowLabel>Date Prescribed</SidebarRowLabel>
        <SidebarRowData>{formatStandardDateLocaleString(drug.prescription_recieved_at)}</SidebarRowData>
      </SidebarRow>
      <SidebarRow>
        <SidebarRowLabel>NDC</SidebarRowLabel>
        <SidebarRowData>{drug.ndc}</SidebarRowData>
      </SidebarRow>
      <SidebarRow>
        <SidebarRowLabel>Quantity</SidebarRowLabel>
        <SidebarRowData>{drug.quantity}</SidebarRowData>
      </SidebarRow>
      <SidebarRow>
        <SidebarRowLabel>Sig</SidebarRowLabel>
        <SidebarRowData>{drug.sig}</SidebarRowData>
      </SidebarRow>
      <SidebarRow>
        <SidebarRowLabel>Refills</SidebarRowLabel>
        <SidebarRowData>{drug.refills}</SidebarRowData>
      </SidebarRow>
      <SidebarRow>
        <SidebarRowLabel>Note</SidebarRowLabel>
        <SidebarRowData>{drug.pharmacy_note}</SidebarRowData>
      </SidebarRow>
    </SidebarSection>
  );
};

export const PharmacyPanel: React.FC<{
  pharmacy?: Pharmacy;
  showUpdatePharmacyButton?: boolean;
  rxId?: string | null;
  onPharmacyUpdate?: () => void;
}> = ({ pharmacy, showUpdatePharmacyButton = false, rxId, onPharmacyUpdate }) => {
  const [showPharmacyUpdateModal, setShowPharmacyUpdateModal] = useState(false);

  return (
    <SidebarSection>
      <SidebarRow>
        <SidebarRowLabel>Pharmacy</SidebarRowLabel>
        <SidebarRowData>{pharmacy?.name || '-'}</SidebarRowData>
      </SidebarRow>
      <SidebarRow>
        <SidebarRowLabel>NPI</SidebarRowLabel>
        <SidebarRowData>{pharmacy?.npi || '-'}</SidebarRowData>
      </SidebarRow>
      <SidebarRow>
        <SidebarRowLabel>Phone</SidebarRowLabel>
        <SidebarRowData>{formatPhoneNumber(pharmacy?.phone || '-')}</SidebarRowData>
      </SidebarRow>
      <SidebarRow>
        <SidebarRowLabel>Fax</SidebarRowLabel>
        <SidebarRowData>{formatPhoneNumber(pharmacy?.fax || '-')}</SidebarRowData>
      </SidebarRow>
      <AddressRow address={pharmacy?.address || null} />
      {showUpdatePharmacyButton && rxId && onPharmacyUpdate && (
        <ButtonRow align={Alignment.Center} fullWidth>
          <Button size="sm" width="full" variant="secondary" onClick={() => setShowPharmacyUpdateModal(true)}>
            Update preferred pharmacy
          </Button>
        </ButtonRow>
      )}
      {showPharmacyUpdateModal && rxId && onPharmacyUpdate && (
        <PharmacySearchModal
          onClose={() => setShowPharmacyUpdateModal(false)}
          onSubmit={onPharmacyUpdate}
          rxId={rxId}
        />
      )}
    </SidebarSection>
  );
};

export const InsurancePanel: React.FC<{ insurance?: Insurance }> = ({ insurance }) => {
  if (!insurance) {
    return;
  }

  return (
    <SidebarSection>
      <SidebarRow>
        <SidebarRowLabel>Insurance</SidebarRowLabel>
        <SidebarRowData>{insurance.name || emptyTableCellCopy}</SidebarRowData>
      </SidebarRow>
      {insurance.form_name && (
        <SidebarRow>
          <SidebarRowLabel>Form Name</SidebarRowLabel>
          <SidebarRowData>{insurance.form_name}</SidebarRowData>
        </SidebarRow>
      )}
      {insurance.member_id && (
        <SidebarRow>
          <SidebarRowLabel>Cardholder ID</SidebarRowLabel>
          <SidebarRowData>{insurance.member_id}</SidebarRowData>
        </SidebarRow>
      )}
      {insurance.bin && (
        <SidebarRow>
          <SidebarRowLabel>RX Bin</SidebarRowLabel>
          <SidebarRowData>{insurance.bin}</SidebarRowData>
        </SidebarRow>
      )}
      {insurance.pcn && (
        <SidebarRow>
          <SidebarRowLabel>PCN</SidebarRowLabel>
          <SidebarRowData>{insurance.pcn}</SidebarRowData>
        </SidebarRow>
      )}
      {insurance.group_id && (
        <SidebarRow>
          <SidebarRowLabel>Group</SidebarRowLabel>
          <SidebarRowData>{insurance.group_id}</SidebarRowData>
        </SidebarRow>
      )}
      {insurance.phone && (
        <SidebarRow>
          <SidebarRowLabel>Phone</SidebarRowLabel>
          <SidebarRowData>{formatPhoneNumber(insurance.phone)}</SidebarRowData>
        </SidebarRow>
      )}
      {insurance.fax && (
        <SidebarRow>
          <SidebarRowLabel>Fax</SidebarRowLabel>
          <SidebarRowData>{formatPhoneNumber(insurance.fax)}</SidebarRowData>
        </SidebarRow>
      )}
    </SidebarSection>
  );
};

const AddressRow: React.FC<{ address: Address | null }> = ({ address }) => {
  let sidebarRowData: string | React.ReactNode = '-';

  if (address) {
    if (address.line1 || address.line2 || address.city || address.state || address.postal_code) {
      const cityAndState = [address.city, address.state].filter(Boolean).join(', ');
      const addressLine3 = [cityAndState, address.postal_code].filter(Boolean).join(' ');

      sidebarRowData = [address.line1, address.line2, addressLine3].filter(Boolean).map((s, i) => (
        <React.Fragment key={i}>
          {s}
          <br />
        </React.Fragment>
      ));
    }
  }

  return (
    <SidebarRow>
      <SidebarRowLabel>Address</SidebarRowLabel>
      <SidebarRowData>{sidebarRowData}</SidebarRowData>
    </SidebarRow>
  );
};

export const ICDCodePanel: React.FC<{ icdCode: string }> = ({ icdCode }) => {
  return (
    <SidebarSection>
      <SidebarRow>
        <SidebarRowLabel>ICD</SidebarRowLabel>
        <SidebarRowData>{icdCode}</SidebarRowData>
      </SidebarRow>
    </SidebarSection>
  );
};
